import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { ImageFilters } from "../../components/admin/ImageFilters";
import { ImageListItem } from "../../components/admin/ImageListItem";
import { BulkActionsBar } from "../../components/admin/BulkActionsBar";
import { Pagination } from "../../components/admin/Pagination";
import { BulkCategoryModal } from "../../components/admin/BulkCategoryModal";
import { BulkGalleryModal } from "../../components/admin/BulkGalleryModal";
import {
  useImages,
  ImageFilters as IImageFilters,
  useToggleVisibility,
  useDeleteImage,
  useBulkUpdateCategories,
  Image,
} from "../../hooks/useImages";
import { useCategories, useSubcategories } from "../../hooks/useCategories";
import { useAlbums } from "../../hooks/useAlbums";
import { getImageId } from "../../utils/imageUtils";

export const AdminImageGalleryPage: React.FC = () => {
  const [filters, setFilters] = useState<IImageFilters>({
    page: 1,
    page_size: 50,
    sort_by: "created_at",
    sort_order: "desc",
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<Image | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [bulkGalleryDialogOpen, setBulkGalleryDialogOpen] = useState(false);
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);

  const { data, isLoading, error } = useImages(filters);
  const toggleVisibility = useToggleVisibility();
  const deleteImage = useDeleteImage();
  const bulkUpdateCategories = useBulkUpdateCategories();
  const { data: categories } = useCategories();
  const { data: subcategories } = useSubcategories();
  const { data: albumsData } = useAlbums({ page_size: 100 });
  const galleries = albumsData?.items || [];

  // Client-side sorting function
  const getSortedImages = (images: Image[]) => {
    if (!images || images.length === 0) return images;

    const sortedImages = [...images].sort((a, b) => {
      const sortBy = filters.sort_by || "created_at";
      const sortOrder = filters.sort_order || "desc";

      let comparison = 0;

      switch (sortBy) {
        case "created_at":
          const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
          comparison = aDate - bDate;
          break;
        case "title":
          comparison = (a.title || "").localeCompare(b.title || "");
          break;
        case "file_size":
          comparison = a.file_size - b.file_size;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return sortedImages;
  };

  const handleSelectionChange = (imageId: string, selected: boolean) => {
    const newSelection = new Set(selectedIds);
    if (selected) {
      newSelection.add(imageId);
    } else {
      newSelection.delete(imageId);
    }
    setSelectedIds(newSelection);
  };

  const handleDeleteClick = (image: Image) => {
    setImageToDelete(image);
    setDeleteDialogOpen(true);
  };

  const handleToggleVisibilityClick = (image: Image) => {
    toggleVisibility.mutate(getImageId(image));
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkPublish = async () => {
    setBulkOperationInProgress(true);
    try {
      const failedIds: string[] = [];
      const promises = Array.from(selectedIds).map(async (id) => {
        const image = data?.items.find((img) => img.id === id);
        if (image && !image.is_public) {
          try {
            await toggleVisibility.mutateAsync(id);
          } catch (error) {
            console.error(`Failed to publish image ${id}:`, error);
            failedIds.push(id);
          }
        }
      });

      await Promise.all(promises);

      // Only clear selections that succeeded
      if (failedIds.length > 0) {
        setSelectedIds(new Set(failedIds));
        alert(`Failed to publish ${failedIds.length} image(s). They remain selected for retry.`);
      } else {
        setSelectedIds(new Set());
      }
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handleBulkUnpublish = async () => {
    setBulkOperationInProgress(true);
    try {
      const failedIds: string[] = [];
      const promises = Array.from(selectedIds).map(async (id) => {
        const image = data?.items.find((img) => img.id === id);
        if (image && image.is_public) {
          try {
            await toggleVisibility.mutateAsync(id);
          } catch (error) {
            console.error(`Failed to unpublish image ${id}:`, error);
            failedIds.push(id);
          }
        }
      });

      await Promise.all(promises);

      // Only clear selections that succeeded
      if (failedIds.length > 0) {
        setSelectedIds(new Set(failedIds));
        alert(`Failed to unpublish ${failedIds.length} image(s). They remain selected for retry.`);
      } else {
        setSelectedIds(new Set());
      }
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Add handler for single delete confirmation
  const confirmDelete = async () => {
    if (!imageToDelete) return;
    await deleteImage.mutateAsync(getImageId(imageToDelete));
    setImageToDelete(null);
    setDeleteDialogOpen(false);
  };

  const confirmBulkDelete = async () => {
    setBulkOperationInProgress(true);
    try {
      const failedIds: string[] = [];
      const promises = Array.from(selectedIds).map(async (id) => {
        try {
          await deleteImage.mutateAsync(id);
        } catch (error) {
          console.error(`Failed to delete image ${id}:`, error);
          failedIds.push(id);
        }
      });

      await Promise.all(promises);

      // Only clear selections that succeeded
      if (failedIds.length > 0) {
        setSelectedIds(new Set(failedIds));
        alert(`Failed to delete ${failedIds.length} image(s). They remain selected for retry.`);
      } else {
        setSelectedIds(new Set());
      }
    } finally {
      setBulkOperationInProgress(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ pb: { xs: 12, sm: 10 } }}>
        {/* Header */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          Manage Images
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4,
          }}
        >
          Browse, edit, and organize your portfolio images
        </Typography>

        {/* Filters */}
        <ImageFilters filters={filters} onFiltersChange={setFilters} />

        {/* Pagination - Top */}
        {data && data.items.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Pagination
              page={data.page}
              pageSize={data.page_size}
              total={data.total}
              onPageChange={handlePageChange}
            />
          </Box>
        )}

        {/* Select All Controls */}
        {data &&
          data.items.length > 0 &&
          (() => {
            const sortedImages = getSortedImages(data.items);
            return (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Button
                  variant="text"
                  disableRipple
                  onClick={() => {
                    if (selectedIds.size === sortedImages.length) {
                      setSelectedIds(new Set());
                    } else {
                      setSelectedIds(
                        new Set(sortedImages.map((img) => getImageId(img)).filter(Boolean) as string[])
                      );
                    }
                  }}
                  sx={{
                    textTransform: "none",
                    color: "text.primary",
                    px: 0,
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      opacity: 0.7,
                    },
                    "&:active": {
                      backgroundColor: "transparent",
                      opacity: 0.5,
                    },
                  }}
                >
                  {selectedIds.size === sortedImages.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {selectedIds.size} of {sortedImages.length} selected
                </Typography>
              </Box>
            );
          })()}

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            Failed to load images. Please try again.
          </Alert>
        )}

        {/* Empty State */}
        {data && data.items.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              color: "text.secondary",
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No images found
            </Typography>
            <Typography variant="body2">
              Upload some images to get started
            </Typography>
          </Box>
        )}

        {/* Success State - Image List */}
        {data &&
          data.items.length > 0 &&
          (() => {
            const sortedImages = getSortedImages(data.items);
            return (
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Showing {sortedImages.length} of {data.total} images
                  </Typography>
                  {filters.sort_by && filters.sort_order && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      Sorted by {filters.sort_by.replace("_", " ")}{" "}
                      {filters.sort_order === "asc" ? "↑" : "↓"}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                    gap: { xs: 1, sm: 1.5 },
                  }}
                >
                  {sortedImages.map((image) => {
                    const imageId = getImageId(image);
                    return (
                      <ImageListItem
                        key={imageId}
                        image={image}
                        selected={selectedIds.has(imageId)}
                        onSelect={handleSelectionChange}
                        onDelete={handleDeleteClick}
                        onToggleVisibility={handleToggleVisibilityClick}
                        sortBy={filters.sort_by}
                        sortOrder={filters.sort_order}
                        categories={categories?.items}
                        subcategories={subcategories?.items}
                        galleries={galleries.map((g) => ({ id: g.id, title: g.title }))}
                      />
                    );
                  })}
                </Box>

                {/* Pagination */}
                <Pagination
                  page={data.page}
                  pageSize={data.page_size}
                  total={data.total}
                  onPageChange={handlePageChange}
                />
              </Box>
            );
          })()}
      </Box>

      {/* Single Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Image?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will permanently delete "
              {imageToDelete?.title || "this image"}" from both the database and
              Cloudinary. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disableRipple>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="text"
              disableRipple
              sx={{
                border: "none",
                boxShadow: "none",
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteDialogOpen && (
        <Dialog
          open={bulkDeleteDialogOpen}
          onClose={() => setBulkDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete {selectedIds.size} images?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will permanently delete {selectedIds.size}{" "}
              {selectedIds.size === 1 ? "image" : "images"} from both the
              database and Cloudinary. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setBulkDeleteDialogOpen(false)}
              disableRipple
            >
              Cancel
            </Button>
            <Button
              onClick={confirmBulkDelete}
              color="error"
              variant="text"
              disableRipple
              sx={{
                border: "none",
                boxShadow: "none",
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Bulk Category Modal */}
      <BulkCategoryModal
        open={bulkCategoryDialogOpen}
        onClose={() => setBulkCategoryDialogOpen(false)}
        onConfirm={async (categoryId, subcategoryId) => {
          await bulkUpdateCategories.mutateAsync({
            image_ids: Array.from(selectedIds),
            category_id: categoryId,
            subcategory_id: subcategoryId,
          });
          setSelectedIds(new Set());
        }}
        selectedCount={selectedIds.size}
        isLoading={bulkUpdateCategories.isPending}
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedIds.size}
        onClearSelection={handleClearSelection}
        onBulkDelete={handleBulkDelete}
        onBulkPublish={handleBulkPublish}
        onBulkUnpublish={handleBulkUnpublish}
        onBulkCategory={() => setBulkCategoryDialogOpen(true)}
        onBulkGallery={() => setBulkGalleryDialogOpen(true)}
        isLoading={bulkOperationInProgress}
      />

      <BulkGalleryModal
        open={bulkGalleryDialogOpen}
        onClose={() => setBulkGalleryDialogOpen(false)}
        selectedImageIds={Array.from(selectedIds)}
        onSuccess={() => {
          setSelectedIds(new Set());
        }}
      />
    </AdminLayout>
  );
};
